import { useTranslate } from '@bynder/localization';
import { EmptyWidget } from './EmptyWidget';
import { useGuideStore } from '../../stores/guideStore';
import { YoutubeWidget as TYoutubeWidget } from '../../types';
import { IconYoutube } from '@bynder/icons';
import styled from 'styled-components';
import { useActiveWidget } from '../../queries/guides';
import { useInspectorStore } from '../../stores/inspectorStore';
import { toPx, useWidgetStyleProperty } from '../guides/Inspector/helpers';
import { CSS_PROPERTIES } from '../guides/Inspector/constants';

interface Props {
  widget: TYoutubeWidget;
  sectionId: string;
  columnIndex: number;
}

const YoutubeWidget = ({ widget, sectionId, columnIndex }: Props) => {
  const { translate } = useTranslate();
  const isEmpty = widget.sourceId === '';
  const isEditMode = useGuideStore(s => s.isEditMode);
  const activeWidget = useActiveWidget();
  const isActiveWidget = widget.id === activeWidget?.id;
  const setWidgetSettings = useInspectorStore(s => s.setWidgetSettings);
  const { value: borderRadius } = useWidgetStyleProperty(
    widget.style.stylesheet,
    'widgets.media.borderRadius',
    CSS_PROPERTIES.BORDER_RADIUS,
  );

  const setActiveWidget = () => {
    setWidgetSettings({
      widgetIndex: 0,
      widgetId: widget?.id,
      sectionId,
      columnIndex,
    });
  };

  const renderYoutubeVideo = () => {
    if (isEmpty) {
      if (isEditMode) {
        return (
          <EmptyWidget
            icon={<IconYoutube />}
            text={translate('WIDGETS_CONSTANTS.EMPTY_WIDGETS.YOUTUBE')}
          />
        );
      }
      return null;
    }

    return (
      <>
        {isEditMode && !isActiveWidget && (
          <SelectionOverlay onClick={setActiveWidget} />
        )}
        <EmbedContainer $borderRadius={borderRadius}>
          <Iframe
            src={`https://www.youtube.com/embed/${widget.sourceId}?feature=oembed&controls=${widget.settings.controls ? '1' : '0'}&autoplay=${widget.settings.autoplay ? '1&mute=1' : '0'}&loop=${widget.settings.loop ? `1&playlist=${widget.sourceId}` : '0'}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            title="youtube-widget"
          />
        </EmbedContainer>
      </>
    );
  };

  return (
    <StyledYoutubeWidgetContainer>
      {renderYoutubeVideo()}
    </StyledYoutubeWidgetContainer>
  );
};

export default YoutubeWidget;

const StyledYoutubeWidgetContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const EmbedContainer = styled.div<{ $borderRadius: string }>`
  border-radius: ${props => toPx(props.$borderRadius)};
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  overflow: hidden;
  /* 16:9 */
  padding-top: 25px;
  height: 0;
`;

const Iframe = styled.iframe`
  position: absolute;
  border: 0;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const SelectionOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: transparent;
  opacity: 0.5;
  z-index: 1;
`;
